/**
 * Global notification context for toast messages using Sonner
 */
import { createContext, useContext, useMemo, useEffect } from 'react';
import { toast } from 'sonner';

type SeverityType = 'info' | 'success' | 'warning' | 'error';

interface NotificationContextType {
  showNotification: (message: string, severity?: SeverityType) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const showNotification = (message: string, severity: SeverityType = 'info') => {
    switch (severity) {
      case 'error':
        toast.error(message);
        break;
      case 'success':
        toast.success(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      case 'info':
      default:
        toast.info(message);
        break;
    }
  };

  const showError = (message: string) => showNotification(message, 'error');
  const showSuccess = (message: string) => showNotification(message, 'success');
  const showWarning = (message: string) => showNotification(message, 'warning');
  const showInfo = (message: string) => showNotification(message, 'info');

  // Listen for global mutation error events from QueryClient
  useEffect(() => {
    const handleMutationError = (event: CustomEvent<{ message: string }>) => {
      showError(event.detail.message);
    };

    window.addEventListener('mutation-error', handleMutationError as EventListener);
    return () => {
      window.removeEventListener('mutation-error', handleMutationError as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showError is stable
  }, []);

  const value = useMemo<NotificationContextType>(
    () => ({
      showNotification,
      showError,
      showSuccess,
      showWarning,
      showInfo,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- functions are stable
    []
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
